import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'react-string-format';
import { constants } from '../../constants';
import useMyMApi from '../../hooks/useMyMApi';
import useValidationRules from '../../hooks/useValidationRules';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton } from '../../ui-elements/MyMButton';
import { MyMLabel } from '../../ui-elements/MyMLabel';
import { MyMModal } from '../../ui-elements/MyMModal';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import ValidatableDatePicker from '../../ui-parts/ValidatableDatePicker';
import { ValidatablePreviewImageDropZone } from '../../ui-parts/ValidatablePreviewImageDropZone';
import ValidatableRichEditor from '../../ui-parts/ValidatableRichEditor';
import ValidatableTextBox from '../../ui-parts/ValidatableTextBox';
import { ValidatableTreeCheckbox } from '../../ui-parts/ValidatableTreeCheckbox';
import { utility } from '../../utility';
import NewsRegistrationInternalPreview from './NewsRegistrationInternalPreview';

/** MyMine Version1.5 NewsRegistrationInternal画面新規追加
 * NewsRegistrationInternal Page Component
 */
const NewsRegistrationInternal = (props) => {
  const { newsId, handleModalClose, callerRefresh } = props;
  const screenId = 'MyM505';
  const apiRequest = useMyMApi(screenId);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSaveHandleValid, setIsSaveHandleValid] = useState(true);
  const [isRegisterHandleValid, setIsRegisterHandleValid] = useState(true);
  const [isReloadRichEditor, setIsReloadRichEditor] = useState(false);
  const [newsInfo, setNewsInfo] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [companyChecked, setCompanyChecked] = useState([]);
  const [productTypeChecked, setProductTypeChecked] = useState([]);
  // 2024.9.16 MyMine Version2.0対応 Modify Start
  const [businessAreaSalesChecked, setBusinessAreaSalesChecked] = useState([]);
  const [businessAreaServiceChecked, setBusinessAreaServiceChecked] = useState([]);
  const [businessAreaAhsChecked, setBusinessAreaAhsChecked] = useState([]);
  const [businessAreaTrainingChecked, setBusinessAreaTrainingChecked] = useState([]);
  // 2024.9.16 MyMine Version2.0対応 Modify End
  const [isSizeError, setIsSizeError] = useState(false);
  const validationRules = useValidationRules();
  const { formState, control, trigger, clearErrors, reset, getFieldState } = useForm({
    mode: 'onSubmit',
  })
  const { controlSettings, messageList } = useWebContext(screenId);

  useEffect(() => {
    const callbackRequestNewsRegistrationInternalLoad = async (result) => {
      const { NewsInfo, ...Conditions } = result;
      setConditions(Conditions);
      if (newsId) {
        // 編集モードの場合
        // 対象Newsのデータが存在しない場合、エラー
        if (Object.values(NewsInfo).every(value => value === null)) {
          handleModalClose();
          window.alert(messageList.MyM_MSG_I_0059);
        }
        // Company Check
        setCompanyChecked([...Conditions.CompanyList.filter(item => Conditions.NewsReferenceCompanyIdList.includes(item.Id)).map(item => String(item.Id))])
        // News情報
        setNewsInfo({
          ...NewsInfo,
          // 画像ファイル名が指定されている場合、画像ファイル取得
          ImageFile: NewsInfo.ImageFileName && new File([await (await fetch(`api/news/image?newsId=${newsId}&fileName=${NewsInfo.ImageFileName}`)).blob()], NewsInfo.ImageFileName)
        });
      } else {
        // 新規モードの場合
        // Company Check 全チェック
        setCompanyChecked([...Conditions.CompanyList.map(item => String(item.Id))])
        // News情報
        setNewsInfo({
          ...NewsInfo,
          // PublishDateにはシステム日付を設定
          PublishDate: new Date().toLocaleDateString()
        });
      }
      setProductTypeChecked([...Conditions.ProductTypeList.filter(item => Conditions.NewsReferenceMetadataIdList.includes(item.Id)).map(item => String(item.Id))])
      // 2024.9.16 MyMine Version2.0対応 Modify Start
      setBusinessAreaSalesChecked([...result.BusinessArea.SalesList.filter(item => result.NewsReferenceMetadataIdList.includes(item.Id)).map(item => String(item.Id))])
      setBusinessAreaServiceChecked([...result.BusinessArea.ServiceList.filter(item => result.NewsReferenceMetadataIdList.includes(item.Id)).map(item => String(item.Id))])
      setBusinessAreaAhsChecked([...result.BusinessArea.AhsList.filter(item => result.NewsReferenceMetadataIdList.includes(item.Id)).map(item => String(item.Id))])
      setBusinessAreaTrainingChecked([...result.BusinessArea.TrainingList.filter(item => result.NewsReferenceMetadataIdList.includes(item.Id)).map(item => String(item.Id))])
      // 2024.9.16 MyMine Version2.0対応 Modify End
      setIsLoading(false);
    }
    const queryPara = `?newsType=${constants.NewsType.InternalNews}${newsId && `&newsId=${newsId}`}`;
    apiRequest.requestJson(`api/news/registration-load${queryPara}`, 'GET', callbackRequestNewsRegistrationInternalLoad)
  }, []);

  // 検証不要なルールに引っかかっていないかをチェック
  const checkErrorMessage = (checkResult, fieldName) => {
    let reCheckResult = checkResult;
    if (!checkResult) {
      // 対象フィールドのエラーメッセージがバリデーション対象外のメッセージの場合エラーメッセージの出力を制御する
      if (getFieldState(fieldName).error?.message === messageList.MyM_MSG_E_0004) {
        clearErrors(fieldName);
        reCheckResult = true;
      }
    }
    return reCheckResult;
  }

  const triggerCheckErrorMessage = async (fieldName) => {
    let checkResult = await trigger(fieldName);
    checkResult = checkErrorMessage(checkResult, fieldName)
    return checkResult;
  }

  // Save(非公開)処理
  const handleSave = async () => {
    setIsRegisterHandleValid(true);
    // フォーム上のエラーをリセットして初期化
    clearErrors();
    // titleフィールドの検証
    const titleCheckresult = await trigger('title')
    // publishDateフィールドの検証
    const publishDateCheckResult = await triggerCheckErrorMessage('publishDate');
    // contentsフィールドの検証
    const contentsCheckResult = await triggerCheckErrorMessage('contents');
    // ImageCaptionフィールドの検証
    const imageCaptionResult = await triggerCheckErrorMessage('imageCaption');
    const result = titleCheckresult && publishDateCheckResult && contentsCheckResult && imageCaptionResult;
    setIsSaveHandleValid(result);
    if (isSizeError) {
      window.alert(messageList.MyM_MSG_E_0003);
      return;
    }
    if (result) {
      if (!window.confirm(messageList.MyM_MSG_I_0030)) return;
      // 検証に通ったらAPIリクエストを実施する
      setIsLoading(true);
      const callbackSuccess = () => {
        handleModalClose();
        window.alert(messageList.MyM_MSG_I_0066);
        setIsLoading(false);
        callerRefresh();
      }
      const callbackFailed = () => {
        setIsLoading(false);
      }

      const formData = makeFormData();
      formData.append('IsPublish', 'false');

      apiRequest.requestForm(`api/news/save-internal-news`, 'POST', callbackSuccess, callbackFailed, formData)
    } else {
      onError();
    }
  };

  // Register(公開)処理
  const handleRegister = async () => {
    setIsSaveHandleValid(true);
    // フォーム上のエラーをリセットして初期化
    clearErrors();
    // titleフィールドの検証
    const titleCheckresult = await trigger('title')
    // publishDateフィールドの検証
    const publishDateCheckResult = await trigger('publishDate');
    // contentsフィールドの検証
    const contentsCheckResult = await trigger('contents');
    // ImageCaptionフィールドの検証
    const imageCaptionResult = await triggerCheckErrorMessage('imageCaption');
    // companyフィールドの検証
    const companyCheckResult = await trigger('checkboxList_company');

    const result = titleCheckresult && publishDateCheckResult && contentsCheckResult && imageCaptionResult && companyCheckResult;
    setIsRegisterHandleValid(result);
    if (isSizeError) {
      result && window.alert(messageList.MyM_MSG_E_0003);
      if (result) return;
    }
    if (result) {

      if (!window.confirm(messageList.MyM_MSG_I_0031)) return;
      setIsLoading(true);
      const callbackSuccess = () => {
        handleModalClose();
        window.alert(messageList.MyM_MSG_I_0067);
        setIsLoading(false);
        callerRefresh();
      }
      const callbackFailed = () => {
        setIsLoading(false);
      }

      const formData = makeFormData();
      formData.append('IsPublish', 'true');
      apiRequest.requestForm(`api/news/save-internal-news`, 'POST', callbackSuccess, callbackFailed, formData)
    } else {
      onError();
    }
  };

  /** News削除処理 */
  const handleDelete = () => {
    if (!window.confirm(messageList.MyM_MSG_I_0081)) return;
    setIsLoading(true);
    const callbackSuccess = () => {
      handleModalClose();
      window.alert(messageList.MyM_MSG_I_0082);
      setIsLoading(false);
      callerRefresh();
    }
    const callbackFailed = () => {
      setIsLoading(false);
    }

    const body = { 'newsId': newsId };
    apiRequest.requestJson(`api/news/delete`, 'POST', callbackSuccess, callbackFailed, body)
  };

  // フォームの状態を初期化
  const handleClear = () => {
    // フォーム上のエラーメッセージ入力値をリセットして初期化
    reset({ title: '', imageCaption: '', contents: '' });
    setNewsInfo(
      {
        Title: '',
        Contents: null,
        PublishDate: new Date().toLocaleDateString(),
        ImageCaption: '',
        ImageFile: null,
      }
    );
    setIsReloadRichEditor(true)
    setCompanyChecked([...conditions.CompanyList.map(item => String(item.Id))])
    setProductTypeChecked([]);
    // 2024.9.16 MyMine Version2.0対応 Modify Start
    setBusinessAreaSalesChecked([])
    setBusinessAreaServiceChecked([])
    setBusinessAreaAhsChecked([])
    setBusinessAreaTrainingChecked([])
    // 2024.9.16 MyMine Version2.0対応 Modify End
    setIsSaveHandleValid(true);
    setIsRegisterHandleValid(true);
    setIsSizeError(false);
  };

  const onError = () => {
    window.alert(messageList.MyM_MSG_E_0003);
  };

  /** ModalCloseイベント */
  const handlePreviewModalClose = () => setIsPreviewModalOpen(false);

  //リクエストボディーの作成
  const makeFormData = () => {
    const formData = new FormData();
    // 2024.9.16 MyMine Version2.0対応 Add Start
    const businessAreaChecked = [...businessAreaSalesChecked, ...businessAreaServiceChecked, ...businessAreaAhsChecked, ...businessAreaTrainingChecked]
    // 2024.9.16 MyMine Version2.0対応 Add End

    for (let key of Object.keys(newsInfo)) {
      if (newsInfo[key])
        formData.append(key, newsInfo[key]);
    }
    companyChecked.forEach((id) => { formData.append('CompanyList[]', id); })
    productTypeChecked.forEach((id) => { formData.append('ProductTypeList[]', id); })
    businessAreaChecked.forEach((id) => { formData.append('BusinessAreaList[]', id); })
    if (newsId)
      formData.append('NewsId', newsId);
    return formData;
  };

  if (newsInfo === null)
    return <MyMProgress />

  return (
    <Box>
      {isLoading && <MyMProgress />}
      <Grid container >
        <Grid item xs={9} sx={{ paddingRight: '20px' }} >
          <MyMLabel>{controlSettings.LabelList.lblTitleTitle}</MyMLabel>
          <ValidatableTextBox
            control={control}
            name='title'
            defaultValue={newsInfo.Title ? newsInfo.Title : ''}
            formState={formState}
            maxLength={256}
            rules={{
              ...validationRules.required(),
              ...validationRules.maxLength(256),
            }}
            onBlur={
              (e) => {
                setNewsInfo({ ...newsInfo, Title: e.target.value })
              }
            }
            onChange={
              async () => {
                !isSaveHandleValid && await trigger('title')
                !isRegisterHandleValid && await trigger('title')
              }
            }
          />
        </Grid>
        <Grid item xs={3}>
          <MyMLabel>{controlSettings.LabelList.lblPublishDateTitle}</MyMLabel>
          <ValidatableDatePicker
            name='publishDate'
            onChange={
              async (newValue) => {
                setNewsInfo({ ...newsInfo, 'PublishDate': newValue !== null ? newValue?.$d.toLocaleString() : null })
                !isSaveHandleValid && await triggerCheckErrorMessage('publishDate')
                !isRegisterHandleValid && await trigger('publishDate')
              }
            }
            width='230px'
            value={newsInfo.PublishDate ? dayjs(newsInfo.PublishDate) : newsInfo.PublishDate}
            control={control}
            formState={formState}
            rules={{
              ...validationRules.required(),
              ...validationRules.date('PublishDate', isRegisterHandleValid),
            }}
          />
        </Grid>
        <Grid item xs={9} sx={{ marginTop: '20px', paddingRight: '20px' }}>
          <MyMLabel>{controlSettings.LabelList.lblContentsTitle}</MyMLabel>
          <ValidatableRichEditor
            controllerName='contents'
            name='contents'
            setEditorValue={
              async (newValue) => {
                setNewsInfo({ ...newsInfo, Contents: newValue })
                !isSaveHandleValid && await triggerCheckErrorMessage('contents')
                !isRegisterHandleValid && await trigger('contents')
              }
            }
            editorValue={newsInfo.Contents}
            control={control}
            formState={formState}
            rules={{
              ...validationRules.required(),
            }}
            isReloadValue={isReloadRichEditor}
            setIsReloadValue={setIsReloadRichEditor}
            editorProps={{
              sx: {
                '.rdw-link-modal': { height: '230px' },
                '.editor-class': { height: '300px', border: '1px solid rgba(0, 0, 0, 0.23)', fontSize: '18px' },
                '.rdw-editor': { height: '100%' },
              },
              // ローカル画像挿入時チェック
              validateLocalImageUpload: (file, contentRaw) => {
                // 画像数チェック
                if (Object.values(contentRaw.entityMap).filter(item => item.type === "IMAGE").length >= conditions.ContentsImageMaxFileCount) {
                  alert(format(messageList.MyM_MSG_E_0084, conditions.ContentsImageMaxFileCount));
                  return false;
                }

                // 画像サイズチェック
                if (file.size > conditions.ContentsImageMaxFileSize) {
                  alert(format(messageList.MyM_MSG_E_0013, Math.floor(utility.convertFileSize(conditions.ContentsImageMaxFileSize, constants.FileSize.MB))));
                  return false;
                }
                return true;
              }
            }}
          />
          <MyMButton onClick={() => setIsPreviewModalOpen(true)} sx={{ borderRadius: '20px', height: '30px', marginTop: '10px', float: 'right' }}>{controlSettings.LabelList.btnPreview}</MyMButton>
        </Grid>
        <Grid item xs={3} sx={{ marginTop: '20px' }}>
          <MyMLabel>{controlSettings.LabelList.lblImageTitle}</MyMLabel>
          <ValidatablePreviewImageDropZone
            height='200px'
            imageFile={newsInfo.ImageFile}
            setImageFile={(file) => setNewsInfo({ ...newsInfo, ImageFile: file })}
            removeText={controlSettings.LabelList.btnRemove}
            isSizeError={isSizeError}
            setIsSizeError={setIsSizeError}
            messageList={messageList}
            utility={utility}
            ThumbnailFileSize={conditions.ThumbnailFileSize}
            constants={constants}
            controlSettings={controlSettings}
          />
          <MyMLabel sx={{ marginTop: '10px' }}>{controlSettings.LabelList.lblImageCaptionTitle}</MyMLabel>
          <ValidatableTextBox
            control={control}
            name='imageCaption'
            defaultValue={newsInfo.ImageCaption ?? ''}
            formState={formState}
            maxLength={1000}
            rules={{
              ...validationRules.maxLength(1000),
            }}
            onBlur={
              (e) => {
                setNewsInfo({ ...newsInfo, ImageCaption: e.target.value })
              }
            }
            onChange={
              async () => {
                !isSaveHandleValid && await trigger('imageCaption')
                !isRegisterHandleValid && await trigger('imageCaption')
              }
            }
          />
        </Grid>
        <Grid item xs={12} >
          <Box sx={{ borderBottom: '3px solid #333', marginTop: '20px' }} />
        </Grid>
        <Grid item xs={3} md={3} lg={3} >
          <Box sx={{ paddingTop: '15px', paddingRight: '20px' }}>
            <MyMLabel>{controlSettings.LabelList.lblCompanyTitle}</MyMLabel>
            <ValidatableTreeCheckbox
              needsCascade={true}
              controllerName='checkboxList_company'
              treeItemList={conditions.CompanyList}
              checkedList={companyChecked}
              setChecked={setCompanyChecked}
              control={control}
              formState={formState}
              trigger={trigger}
              clearText={controlSettings.LabelList.btnCompanyClearAll}
              height='370px'
              rules={{ ...validationRules.listRequired(companyChecked) }}
              handleValid={isRegisterHandleValid}
            />
          </Box>
        </Grid>
        <Grid item xs={9} md={9} lg={9} >
          <Box sx={{ borderLeft: '3px solid #333', height: '100%' }}>
            <Box sx={{ margin: '15px 0 10px 20px' }}>
              <MyMLabel>{controlSettings.LabelList.lblMetaDataTitle}</MyMLabel>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} >
                <Box sx={{ marginLeft: '20px' }}>
                  <MyMLabel>{controlSettings.LabelList.lblProductTypeTitle}</MyMLabel>
                  <ValidatableTreeCheckbox
                    controllerName='checkboxList_productType'
                    needsCascade={true}
                    treeItemList={conditions.ProductTypeList}
                    checkedList={productTypeChecked}
                    setChecked={setProductTypeChecked}
                    control={control}
                    formState={formState}
                    clearText={controlSettings.LabelList.btnProductTypeClearAll}
                    trigger={trigger}
                    height='340px'
                    handleValid={isRegisterHandleValid}
                  />
                </Box>
              </Grid>
              <Grid item xs={6} >
                <Box >
                  {/*2024.9.16 MyMine Version2.0対応 Modify Start*/}
                  <MyMLabel>{controlSettings.LabelList.lblBusinessAreaSalesTitle}</MyMLabel>
                  <ValidatableTreeCheckbox
                    controllerName='checkboxList_businessArea_sales'
                    needsCascade={true}
                    treeItemList={conditions.BusinessArea.SalesList}
                    checkedList={businessAreaSalesChecked}
                    setChecked={setBusinessAreaSalesChecked}
                    control={control}
                    formState={formState}
                    clearText={controlSettings.LabelList.btnBusinessAreaSalesClearAll}
                    trigger={trigger}
                    height='340px'
                    handleValid={isRegisterHandleValid}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid container spacing={2} mt={1 }>
          <Grid item xs={4} >
            <Box >
              <MyMLabel>{controlSettings.LabelList.lblBusinessAreaServicePartsTitle}</MyMLabel>
              <ValidatableTreeCheckbox
                controllerName='checkboxList_businessArea_service'
                needsCascade={true}
                treeItemList={conditions.BusinessArea.ServiceList}
                checkedList={businessAreaServiceChecked}
                setChecked={setBusinessAreaServiceChecked}
                control={control}
                formState={formState}
                clearText={controlSettings.LabelList.btnBusinessAreaServicePartsClearAll}
                trigger={trigger}
                height='340px'
                handleValid={isRegisterHandleValid}
              />
            </Box>
          </Grid>

          <Grid item xs={4} >
            <Box >
              <MyMLabel>{controlSettings.LabelList.lblBusinessAreaAhsTitle}</MyMLabel>
              <ValidatableTreeCheckbox
                controllerName='checkboxList_businessArea_ahs'
                needsCascade={true}
                treeItemList={conditions.BusinessArea.AhsList}
                checkedList={businessAreaAhsChecked}
                setChecked={setBusinessAreaAhsChecked}
                control={control}
                formState={formState}
                clearText={controlSettings.LabelList.btnBusinessAreaAhsClearAll}
                trigger={trigger}
                height='340px'
                handleValid={isRegisterHandleValid}
              />
            </Box>
          </Grid>
          <Grid item xs={4} >
            <Box >
              <MyMLabel>{controlSettings.LabelList.lblBusinessAreaTrainingTitle}</MyMLabel>
              <ValidatableTreeCheckbox
                controllerName='checkboxList_businessArea_training'
                needsCascade={true}
                treeItemList={conditions.BusinessArea.TrainingList}
                checkedList={businessAreaTrainingChecked}
                setChecked={setBusinessAreaTrainingChecked}
                control={control}
                formState={formState}
                clearText={controlSettings.LabelList.btnBusinessAreaTrainingClearAll}
                trigger={trigger}
                height='340px'
                handleValid={isRegisterHandleValid}
              />
            </Box>
          </Grid>
        </Grid>
        {/*2024.9.16 MyMine Version2.0対応 Modify End*/}
      </Grid>
      <Stack direction='row' justifyContent='space-between' alignItems='center' className='mt-3' sx={{ 'button': { minWidth: '180px' } }}>
        <Stack direction='row' justifyContent='flex-start' spacing={3} >
          <MyMButton onClick={handleClear} >{controlSettings.LabelList.btnClear}</MyMButton>
        </Stack>
        <Stack direction='row' justifyContent='flex-end' spacing={3}>
          {props.newsId && <MyMButton onClick={handleDelete}>{controlSettings.LabelList.btnDelete}</MyMButton>}
          <MyMButton onClick={handleSave}>{controlSettings.LabelList.btnSave}</MyMButton>
          <MyMButton onClick={handleRegister}>{controlSettings.LabelList.btnRegister}</MyMButton>
          <MyMButton onClick={handleModalClose}>{controlSettings.LabelList.btnClose}</MyMButton>
        </Stack>
      </Stack>

      <MyMModal open={isPreviewModalOpen} onClose={handlePreviewModalClose} width='90%' height='90%'>
        <NewsRegistrationInternalPreview newsInfo={newsInfo} handleModalClose={handlePreviewModalClose} controlSettings={controlSettings} />
      </MyMModal>
    </Box>
  );
}

export default NewsRegistrationInternal